import { useCallback } from "react";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { TaskTemplate } from "@common/types/taskTemplate";
import { didRequestReachServer } from "@api/apiError";
import { useBulkSaveCloseTemplateTasksMutation } from "@api/taskTemplate.api";
import {
    computeTaskEdgeDiff,
    hasDependencyChanges,
    hasEdgeChanges,
    hasRelationChanges,
    type TaskEdgeDiff,
} from "./closeTemplateTaskEdgeDiff";
import { isTaskSavable } from "./closeTemplateTaskLinks";
import { LocalTask } from "./closeTemplateTasksDrawer.types";

/** JSON.stringify with sorted keys so key order from JSONB round-trips doesn't cause false positives. */
function stableStringify(value: unknown): string {
    return JSON.stringify(value, (_, v) =>
        v && typeof v === "object" && !Array.isArray(v)
            ? Object.fromEntries(
                  Object.entries(v).sort(([a], [b]) => a.localeCompare(b)),
              )
            : v,
    );
}

type PersistedLocalTask = LocalTask & { persistedId: string };

interface TaskDiff extends TaskEdgeDiff {
    toCreate: LocalTask[];
    toUpdate: PersistedLocalTask[];
    toDelete: PersistedLocalTask[];
    appliedSourceTemplateVersionById: Map<string, number>;
}

/** Computes the create/update/delete diff between fetched and local tasks. */
export function computeTaskDiff(
    fetchedTasks: TaskTemplate[] | undefined,
    localTasks: LocalTask[],
): TaskDiff {
    const originalById = new Map(
        (fetchedTasks ?? []).map((task) => [task.id, task]),
    );

    const toCreate = localTasks.filter(
        (task) => !task.persistedId && isTaskSavable(task),
    );

    const toUpdate = localTasks.filter((task): task is PersistedLocalTask => {
        if (!task.persistedId || task.isDeleted) {
            return false;
        }
        const originalTask = originalById.get(task.persistedId);
        return (
            !!originalTask &&
            (originalTask.title !== task.name ||
                originalTask.closeStageId !== task.closeStageId ||
                originalTask.assigneeId !== task.assigneeId ||
                originalTask.assigneeRole !== task.assigneeRole ||
                originalTask.externalDeadline !== task.externalDeadline ||
                originalTask.deadlineAnchor !== task.deadlineAnchor ||
                originalTask.estimatedTime !== task.estimatedTime ||
                originalTask.type !== task.taskType ||
                originalTask.initialMessage !== task.initialMessage ||
                originalTask.order !== task.order ||
                originalTask.sourceTemplateVersion !==
                    task.sourceTemplateVersion ||
                stableStringify(originalTask.viewConfig) !==
                    stableStringify(task.viewConfig))
        );
    });

    const appliedSourceTemplateVersionById = new Map<string, number>();
    for (const { persistedId, sourceTemplateVersion } of toUpdate) {
        if (
            sourceTemplateVersion !== null &&
            originalById.get(persistedId)?.sourceTemplateVersion !==
                sourceTemplateVersion
        ) {
            appliedSourceTemplateVersionById.set(
                persistedId,
                sourceTemplateVersion,
            );
        }
    }

    const toDelete = localTasks.filter(
        (task): task is PersistedLocalTask =>
            task.persistedId !== undefined && task.isDeleted,
    );

    return {
        toCreate,
        toUpdate,
        toDelete,
        appliedSourceTemplateVersionById,
        ...computeTaskEdgeDiff(fetchedTasks, localTasks),
    };
}

/** Returns true if localTasks differ from fetchedTasks in any way that would be saved. */
export function computeHasUnsavedChanges(
    fetchedTasks: TaskTemplate[] | undefined,
    localTasks: LocalTask[],
): boolean {
    const diff = computeTaskDiff(fetchedTasks, localTasks);
    return (
        diff.toCreate.length > 0 ||
        diff.toUpdate.length > 0 ||
        diff.toDelete.length > 0 ||
        hasEdgeChanges(diff)
    );
}

interface UseCloseTemplateTasksSaveArgs {
    closeTemplateId: string | null;
    closeTemplateName?: string;
    fetchedTasks: TaskTemplate[] | undefined;
    localTasks: LocalTask[];
    onSuccess: () => void;
    onSaveAndApply?: () => void;
}

export const useCloseTemplateTasksSave = ({
    closeTemplateId,
    closeTemplateName,
    fetchedTasks,
    localTasks,
    onSuccess,
    onSaveAndApply,
}: UseCloseTemplateTasksSaveArgs) => {
    const { toast: successToast, error: errorToast } = useToaster();

    const bulkSaveMutation = useBulkSaveCloseTemplateTasksMutation(
        closeTemplateId ?? "",
    );
    const isSaving = bulkSaveMutation.isPending;

    const buildPayload = useCallback(
        () => computeTaskDiff(fetchedTasks, localTasks),
        [fetchedTasks, localTasks],
    );

    const persist = useCallback(
        async (onDone: () => void) => {
            if (!closeTemplateId || bulkSaveMutation.isPending) {
                return;
            }

            const diff = buildPayload();
            const {
                toCreate,
                toUpdate,
                toDelete,
                appliedSourceTemplateVersionById,
                dependencies,
                relations,
            } = diff;
            const nameLabel = closeTemplateName
                ? ` for "${closeTemplateName}"`
                : "";

            if (
                toCreate.length === 0 &&
                toUpdate.length === 0 &&
                toDelete.length === 0 &&
                !hasEdgeChanges(diff)
            ) {
                successToast(`Tasks saved${nameLabel}`);
                onDone();
                return;
            }

            try {
                await bulkSaveMutation.mutateAsync({
                    closeTemplateId,
                    creates: toCreate.map((task) => ({
                        localId: task.localId,
                        title: task.name.trim(),
                        assigneeRole: task.assigneeRole ?? null,
                        assigneeId: task.assigneeId ?? null,
                        closeStageId: task.closeStageId ?? null,
                        externalDeadline: task.externalDeadline ?? null,
                        deadlineAnchor: task.deadlineAnchor ?? null,
                        estimatedTime: task.estimatedTime ?? null,
                        type: task.taskType ?? null,
                        initialMessage: task.initialMessage ?? null,
                        viewConfig: task.viewConfig ?? null,
                        sourceTemplateId: task.sourceTemplateId,
                        order: task.order,
                    })),
                    updates: toUpdate.map((task) => ({
                        id: task.persistedId,
                        title: task.name.trim(),
                        assigneeId: task.assigneeId ?? null,
                        assigneeRole: task.assigneeRole ?? null,
                        closeStageId: task.closeStageId ?? null,
                        externalDeadline: task.externalDeadline ?? null,
                        deadlineAnchor: task.deadlineAnchor ?? null,
                        estimatedTime: task.estimatedTime ?? null,
                        type: task.taskType ?? null,
                        initialMessage: task.initialMessage ?? null,
                        viewConfig: task.viewConfig ?? null,
                        order: task.order,
                        appliedSourceTemplateVersion:
                            appliedSourceTemplateVersionById.get(
                                task.persistedId,
                            ),
                    })),
                    deletes: toDelete.map((task) => task.persistedId),
                    ...(hasDependencyChanges(diff) ? { dependencies } : {}),
                    ...(hasRelationChanges(diff) ? { relations } : {}),
                });

                successToast(`Tasks saved${nameLabel}`);
                onDone();
            } catch (error) {
                if (!didRequestReachServer(error)) {
                    errorToast(`Failed to save tasks${nameLabel}`);
                }
            }
        },
        [
            closeTemplateId,
            closeTemplateName,
            bulkSaveMutation,
            buildPayload,
            successToast,
            errorToast,
        ],
    );

    const handleSave = useCallback(
        async () => await persist(onSuccess),
        [persist, onSuccess],
    );

    const handleSaveAndApply = useCallback(
        async () => await persist(onSaveAndApply ?? onSuccess),
        [persist, onSaveAndApply, onSuccess],
    );

    return { handleSave, handleSaveAndApply, isSaving };
};
